import { ACTIVE_PROFILE_STORAGE_KEY, BUSINESS_TYPE, EMPTY_PROFILE } from '@/constants';
import { getState } from '@/store';
import type { PersistedActiveProfile, Profile } from '@/types';
import { type Notification, PL_LOG_GROUPS, logger, notify } from '@/utils';
import { LOG_LEVEL, storage } from '@b2x/shared';
import { resolveBusinessType } from './resolve-business-type';

export async function resolveActiveProfile(availableProfiles: Profile[]): Promise<Profile> {
  const businessType = await resolveBusinessType();
  const profileId = storage.local.get<PersistedActiveProfile>(ACTIVE_PROFILE_STORAGE_KEY)?.id;

  const activeProfile = availableProfiles.find(
    (profile) => profile.id === profileId && profile.businessType === businessType,
  ) ??
    availableProfiles.find((profile) => profile.businessType === businessType) ?? { ...EMPTY_PROFILE, businessType };

  logger('Resolved active profile:', {
    args: [activeProfile],
    group: PL_LOG_GROUPS.CORE,
    level: LOG_LEVEL.INFO,
  });

  notifyProfileContextChange(activeProfile);

  return activeProfile;
}

function notifyProfileContextChange(activeProfile: Profile): void {
  const { activeProfile: prevActiveProfile, isPending } = getState();
  const isSameContext = prevActiveProfile.businessType === activeProfile.businessType;
  const isFeatureEnabled = storage.local.get<boolean>('b2x.ff.enable-profile-change-notification') ?? false;

  if (isSameContext || isPending || !isFeatureEnabled) {
    return;
  }

  const notification: Notification = {
    message: 'You are now in the private customer area',
    icon: 'bds/user/24',
    variant: 'basic',
    lifetime: 20_000,
  };

  const hasNoCompanies = activeProfile.id === 'DEFAULT_B2B';
  const isB2B = activeProfile.businessType === BUSINESS_TYPE.B2B;

  if (isB2B) {
    notification.message = `You are now in the business customer area. Enjoy the discounts of your company profile: ${activeProfile.displayName}`;
    notification.icon = 'company/24';

    if (hasNoCompanies) {
      notification.message = 'You are now in the business customer area';
    }
  }

  logger('Sending notification to the user as the profile context has changed unintendedly', {
    group: PL_LOG_GROUPS.CORE,
    level: LOG_LEVEL.INFO,
  });

  notify(notification);
}
